import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useT, useLang } from "@/lib/i18n";

const IDS = ["prog-td-detail", "prog-mt-detail", "prog-pm-detail"];

export function ProgrammesDetailed({ onEnquire }) {
  const t = useT();
  const { lang } = useLang();
  const isHi = lang === "hi";
  const progs = [t("progD.p1"), t("progD.p2"), t("progD.p3")];

  return (
    <section id="curriculum" className="relative py-16 md:py-24 bg-secondary/50">
      <div className="max-w-[90rem] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-10 md:mb-14"
        >
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-sans font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              <span className="font-hindi font-bold">{t("progD.heading")}</span>
            </h2>
            <div className={`mt-4 text-xl sm:text-2xl md:text-3xl text-muted-foreground ${isHi ? "font-hindi" : "font-display italic"}`}>
              {t("progD.subHeading")}
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <div className={`inline-flex items-center gap-2 bg-[#FFCC00] text-black px-4 py-2 rounded-full label-overline text-[11px] ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>
              {t("progD.regOpen")}
            </div>
            <div className="mt-3">
              <div className={`text-3xl md:text-4xl font-black leading-none ${isHi ? "font-hindi" : "font-sans"}`}>{t("progD.duration")}</div>
              <div className={`label-overline text-muted-foreground mt-1 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("progD.durationLabel")}</div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {progs.map((p, i) => (
            <motion.article
              key={IDS[i]}
              data-testid={IDS[i]}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="brochure-card card-hover bg-card border border-border overflow-hidden flex flex-col"
            >
              <div className="flex items-start justify-between pb-4 border-b border-border">
                <div>
                  <div className={`label-overline text-muted-foreground ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{p.stream}</div>
                </div>
                <span className="bg-[#0A0A0A] text-white font-mono text-[10px] tracking-[0.18em] px-3 py-1.5 rounded-md">{p.code}</span>
              </div>

              <div className="pt-5 pb-3">
                <h3 className={`font-extrabold text-2xl md:text-[26px] leading-tight tracking-tight ${isHi ? "font-hindi" : "font-sans"}`}>{p.title}</h3>
                <div className="font-hindi text-lg text-muted-foreground mt-1">{p.hindi}</div>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 py-4">
                {p.modules.map((m) => (
                  <div key={m} className={`flex items-start gap-2 text-[13px] text-foreground/80 ${isHi ? "font-hindi" : ""}`}>
                    <span className="text-[#FFCC00] mt-0.5">●</span>
                    <span>{m}</span>
                  </div>
                ))}
              </div>

              <div className="mt-auto pt-4 border-t border-border">
                <div className="bg-[#FFCC00] -mx-7 px-7 py-2.5 flex items-center justify-between">
                  <span className={`label-overline text-black text-[10px] ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("progD.durationPill")}</span>
                </div>
                <div className="pt-4">
                  <div className={`label-overline text-muted-foreground mb-2 text-[10px] ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("progD.careersLabel")}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.careers.slice(0, 4).map((c) => (
                      <span key={c} className={`text-[11px] px-2.5 py-1 rounded-full bg-muted text-muted-foreground ${isHi ? "font-hindi text-[12px]" : ""}`}>{c}</span>
                    ))}
                    <span className={`text-[11px] px-2.5 py-1 rounded-full bg-muted text-muted-foreground ${isHi ? "font-hindi text-[12px]" : ""}`}>{t("progD.moreCareer")}</span>
                  </div>
                </div>
                <button onClick={onEnquire} className={`mt-4 w-full btn-pill group text-[12px] ${isHi ? "font-hindi" : ""}`}>
                  {t("progD.enquireFor")} {p.title}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
